import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import config from '../config';

const BatmanAuthContext = createContext(null);

export function BatmanAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem(config.accessTokenKey));
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem(config.refreshTokenKey));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const storeTokens = useCallback((access, refresh) => {
    setAccessToken(access);
    setRefreshToken(refresh);
    if (access) {
      localStorage.setItem(config.accessTokenKey, access);
    } else {
      localStorage.removeItem(config.accessTokenKey);
    }
    if (refresh) {
      localStorage.setItem(config.refreshTokenKey, refresh);
    } else {
      localStorage.removeItem(config.refreshTokenKey);
    }
  }, []);

  const clearAuth = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    localStorage.removeItem(config.accessTokenKey);
    localStorage.removeItem(config.refreshTokenKey);
  }, []);

  const validateBatmanAccount = useCallback((account) => {
    if (!account || account.segment !== config.requiredSegment) {
      throw new Error('This app is only for Batman users.');
    }
    return account;
  }, []);

  const login = useCallback(async (username, password) => {
    const normalizedUsername = username.toLowerCase().trim();
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${config.backendBaseUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: normalizedUsername, password }),
      });

      const auth = await response.json();
      if (!response.ok) {
        throw new Error(auth.error?.message || 'Login failed');
      }

      const account = validateBatmanAccount(auth.account);
      storeTokens(auth.accessToken, auth.refreshToken);
      setUser(account);

      return { success: true };
    } catch (e) {
      setError(e.message || 'Login failed');
      return { success: false, error: e.message || 'Login failed' };
    } finally {
      setIsLoading(false);
    }
  }, [storeTokens, validateBatmanAccount]);

  const signup = useCallback(async (username, password) => {
    const normalizedUsername = username.toLowerCase().trim();
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`${config.backendBaseUrl}/api/auth/register/batman`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: normalizedUsername, password }),
      });

      const auth = await response.json();
      if (!response.ok) {
        throw new Error(auth.error?.message || 'Signup failed');
      }

      const account = validateBatmanAccount(auth.account);
      storeTokens(auth.accessToken, auth.refreshToken);
      setUser(account);

      return { success: true };
    } catch (e) {
      setError(e.message || 'Signup failed');
      return { success: false, error: e.message || 'Signup failed' };
    } finally {
      setIsLoading(false);
    }
  }, [storeTokens, validateBatmanAccount]);

  const refreshAccessToken = useCallback(async () => {
    if (!refreshToken) {
      clearAuth();
      return null;
    }

    try {
      const response = await fetch(`${config.backendBaseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      const data = await response.json();
      if (!response.ok) {
        clearAuth();
        return null;
      }

      storeTokens(data.accessToken, data.refreshToken);
      setUser(validateBatmanAccount(data.account));
      return data.accessToken;
    } catch (e) {
      clearAuth();
      return null;
    }
  }, [refreshToken, clearAuth, storeTokens, validateBatmanAccount]);

  const fetchAccount = useCallback(async () => {
    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${config.backendBaseUrl}/api/account`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          const retryResponse = await fetch(`${config.backendBaseUrl}/api/account`, {
            headers: { Authorization: `Bearer ${newToken}` },
          });
          const retryData = await retryResponse.json();
          if (retryResponse.ok) {
            setUser(validateBatmanAccount(retryData.account));
          }
        }
        return;
      }

      const data = await response.json();
      if (response.ok) {
        setUser(validateBatmanAccount(data.account));
      } else {
        clearAuth();
      }
    } catch (e) {
      setError(e.message || 'Failed to fetch account');
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, refreshAccessToken, validateBatmanAccount, clearAuth]);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  const logout = useCallback(async () => {
    try {
      if (refreshToken) {
        await fetch(`${config.backendBaseUrl}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
      }
    } catch (e) {
      // ignore
    } finally {
      clearAuth();
    }
  }, [refreshToken, clearAuth]);

  const value = {
    user,
    accessToken,
    isAuthenticated: !!user && !!accessToken,
    isLoading,
    error,
    setError,
    login,
    signup,
    fetchAccount,
    logout,
    refreshAccessToken,
  };

  return <BatmanAuthContext.Provider value={value}>{children}</BatmanAuthContext.Provider>;
}

export function useBatmanAuth() {
  const context = useContext(BatmanAuthContext);
  if (!context) throw new Error('useBatmanAuth must be used inside BatmanAuthProvider');
  return context;
}
